import { AlertTriangle } from 'lucide-react';
import { emailDomain, evaluateOrgChoice, selectableOrgs, type OrgOption } from '../features/admin/orgAssignment';

/**
 * The required "Organisation" field used when approving an account, adding a
 * user, and on the All Users "Choose organisation" action.
 *
 * Only active organisations are offered. When the chosen organisation's
 * domains do not include the person's email domain, a warning appears and the
 * administrator must tick the confirmation before the dialog can be submitted;
 * the server refuses the same mismatch unless that confirmation is sent.
 */
export default function OrganisationPicker({
  email,
  orgs,
  orgsLoading,
  value,
  onChange,
  acknowledged,
  onAcknowledgedChange,
  disabled,
}: {
  email: string;
  orgs: OrgOption[];
  orgsLoading: boolean;
  value: string;
  onChange: (orgId: string) => void;
  acknowledged: boolean;
  onAcknowledgedChange: (value: boolean) => void;
  disabled?: boolean;
}) {
  const options = selectableOrgs(orgs);
  const verdict = evaluateOrgChoice({ email, orgId: value, orgs, acknowledged });
  const chosen = options.find((o) => o.orgId === value);
  const domain = emailDomain(email);

  return (
    <div className="space-y-3">
      <label className="block text-sm font-semibold text-[#274249]">
        Organisation
        <select
          required
          value={value}
          disabled={disabled || orgsLoading}
          onChange={(e) => {
            onChange(e.target.value);
            onAcknowledgedChange(false);
          }}
          className="mt-2 h-11 w-full rounded-xl border border-[#CAD2D5] bg-white px-3 font-normal outline-none focus:border-[var(--brand-primary)] disabled:bg-[#F1F4F5]"
        >
          <option value="">{orgsLoading ? 'Loading organisations...' : 'Choose an organisation'}</option>
          {options.map((o) => (
            <option key={o.orgId} value={o.orgId}>
              {o.name || o.orgId}
              {o.domains && o.domains.length > 0 ? ` (${o.domains.join(', ')})` : ''}
            </option>
          ))}
        </select>
      </label>
      {!orgsLoading && options.length === 0 && (
        <p className="text-sm text-red-700">
          There are no active organisations to choose from. Create one on the Organizations page first.
        </p>
      )}
      {verdict.domainMismatch && chosen && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" role="alert">
          <p className="flex items-start gap-2 font-semibold">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>
              {domain ? `The email domain ${domain}` : 'This email address'} is not one of the domains of{' '}
              {chosen.name || chosen.orgId}
              {chosen.domains && chosen.domains.length > 0
                ? ` (${chosen.domains.join(', ')}).`
                : ', which has no domains set.'}
            </span>
          </p>
          <label className="mt-3 flex items-start gap-3 font-normal">
            <input
              type="checkbox"
              checked={acknowledged}
              disabled={disabled}
              onChange={(e) => onAcknowledgedChange(e.target.checked)}
              className="mt-1"
            />
            <span>
              I confirm this person belongs to {chosen.name || chosen.orgId} even though their email domain is not
              one of its domains.
            </span>
          </label>
        </div>
      )}
    </div>
  );
}
